import React, { useState } from 'react';
import { NotificationItem, updateNotificationVisibility } from '../services/notification-service';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
// import { useCommonStore } from 'state/store/common/useCommonStore'
import { CheckCircle2 } from 'lucide-react';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { useNotificationFeedStore } from 'state/store/notifications/use-notification-feed-store';
import { useToast } from 'hooks/use-toast';
import { NotificationType } from 'features/chat/types/notification-types';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { useNavigate } from 'react-router-dom';



function GroupNotification({ notification, onClose }: { notification: NotificationItem, onClose: () => void }) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { theme } = useTheme();
    const navigate = useNavigate();

    const { fetchAndSetNotification, currentPage } = useNotificationFeedStore();
    const sidebarConversationsList = useChatStore((state) => state.sidebarConversationsList);


    const formatTimestamp = (timestamp: string) => {
        try {
            return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
        } catch {
            return timestamp;
        }
    };

    const activity = notification.activity?.verb;
    // console.log('activity', activity);

    const getNotificationConfig = () => {
        const translationKey = notification?.activity?.extraContext?.translationKey;
        const notificationType = notification.activity?.object?.type;
        const displayName = notification?.activity?.object?.displayName;

        const buildMessage = (params: Record<string, string>, fallback: string): string => {
            return t(translationKey || '', params) || fallback;
        };

        // Get conversation name for COLLABORATION type
        const getConversationName = (): string => {
            if (notificationType !== NotificationType.COLLABORATION) return '';
            const conversationId = notification?.activity?.object?.objectId;
            const conversation = sidebarConversationsList.find(
                (item) => item.ConversationId === conversationId
            );
            return conversation?.ConversationTitle || 'Conversation';
        };

        if (activity === 'ADD') {
            if (notificationType === NotificationType.GROUP || notificationType === NotificationType.ORGANISATION) {
                return {
                    message: buildMessage(
                        { organizationName: `${displayName}` },
                        `You were added to ${displayName}`
                    ),
                };
            }
            if (notificationType === NotificationType.COLLABORATION) {
                const conversationId = notification?.activity?.object?.objectId;
                const conversationName = getConversationName();
                return {
                    message: buildMessage(
                        { conversationName: `${conversationName}` },
                        `You were added to the chat ${conversationName}`
                    ),
                    conversationId,
                };
            }

            if (notificationType === NotificationType.PLAN) {
                return {
                    message: buildMessage(
                        { planName: `${displayName}` },
                        `Your plan was upgraded to ${displayName}`
                    ),
                };
            }

            else if (notificationType === NotificationType.LOGIN) {

                const browserName = notification?.activity?.extraContext?.browser;
                const deviceName = notification?.activity?.extraContext?.device;

                return {
                    message: buildMessage(
                        { browserName: `${browserName}`, deviceName: `${deviceName}` },
                        `New login detected from ${browserName} on ${deviceName}.`
                    ),
                };
            }
            else if (notificationType === NotificationType.ROLE) {
                const organizationName = notification?.activity?.target?.displayName;

                return {
                    message: buildMessage(
                        { organizationName: `${organizationName}` },
                        `You were granted admin privileges for the organization ${organizationName}`
                    ),
                };
            }
        } else if (activity === 'REMOVE') {
            if (notificationType === NotificationType.GROUP || notificationType === NotificationType.ORGANISATION) {
                return {
                    message: buildMessage(
                        { organizationName: `${displayName}` },
                        `You were removed from ${displayName}`
                    ),
                };
            }
            else if (notificationType === NotificationType.COLLABORATION) {
                const conversationName = getConversationName();
                return {
                    message: buildMessage(
                        { conversationName: `${conversationName}` },
                        `You were removed from the chat ${conversationName}`
                    ),
                };
            }
            else if (notificationType === NotificationType.SUBSCRIPTION) {
                return {
                    message: buildMessage(
                        {},
                        'Your subscription has expired. Renew to continue using EagleGPT.'
                    ),
                };
            }
            else if (notificationType === NotificationType.PLAN) {
                return {
                    message: buildMessage(
                        { planName: `${displayName}` },
                        `Your plan was downgraded to ${displayName}`
                    ),
                };
            } else if (notificationType === NotificationType.ROLE) {
                const organizationName = notification?.activity?.target?.displayName;
                return {
                    message: buildMessage(
                        { organizationName: `${organizationName}` },
                        `You were removed from admin privileges for ${organizationName}`
                    ),
                };
            }
        }
        else if (activity === 'SHARE') {
            if (notificationType === NotificationType.FILE) {
                return {
                    message: buildMessage(
                        { fileName: `${displayName}` },
                        `A file ${displayName} was shared with you`
                    ),
                };
            }
        }

        else if (activity === 'CHANGE') {

            if (notificationType === NotificationType.SUBSCRIPTION) {
                return {
                    message: buildMessage(
                        { planName: `${displayName}` },
                        `Your subscription plan was upgraded to ${displayName}`
                    ),
                };
            }
            else if (notificationType === NotificationType.PASSWORD) {
                return {
                    message: buildMessage(
                        { password: `${displayName}` },
                        `Your password was successfully changed.`
                    ),
                };
            }

        }

        else if (activity === 'Share') {

            if (notificationType === NotificationType.FILE) {
                return {
                    message: buildMessage(
                        { planName: `${displayName}` },
                        `Your subscription plan was upgraded to ${displayName}`
                    ),
                };
            }


        }
        else if (activity === 'ACCEPT' && notificationType === NotificationType.SUBSCRIPTION) {
            return {
                message: buildMessage(
                    { planName: `${displayName}` },
                    `Your payment for ${displayName} was successful.`
                ),
            };
        }

        else if (activity === 'FAILED' && notificationType === NotificationType.SUBSCRIPTION) {
            return {
                message: buildMessage(
                    { planName: `${displayName}` },
                    `Your payment for ${displayName} failed.`
                ),
            };
        }

        else if (activity === 'CANCEL' && notificationType === NotificationType.SUBSCRIPTION) {
            return {
                message: buildMessage(
                    {},
                    'Your subscription was cancelled.'
                ),
            };
        }
        else {
            return {
                message: buildMessage({}, 'Notification'),
            };
        }
    };

    const config = getNotificationConfig();
    const isUnread = !notification.read;

    const handleMarkAsRead = async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (isMarkingAsRead || notification.read) {
            return;
        }

        setIsMarkingAsRead(true);
        try {
            const configurationSettings = useConfigStore.getState().configurationSettings;
            if (!configurationSettings?.blocksKey) {
                throw new Error('Configuration settings or blocksKey is not available');
            }

            const itemId = notification.itemId || notification.id;
            if (!itemId) {
                throw new Error('Notification itemId is not available');
            }

            const payload = {
                projectKey: configurationSettings.blocksKey,
                itemIds: [itemId],
                seen: true,
                read: true,
            };

            const response = await updateNotificationVisibility(configurationSettings, payload);

            if (response.isSuccess) {

                // Refresh notifications to update the UI - stay on current page
                await fetchAndSetNotification(currentPage);
            } else {
                throw new Error('Failed to mark notification as read');
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error marking notification as read:', error);
            toast({
                variant: 'destructive',
                title: t('ERROR') || 'Error',
                description: t('FAILED_TO_MARK_AS_READ') || 'Failed to mark notification as read',
            });
        } finally {
            setIsMarkingAsRead(false);
        }
    };

    return (
        <div
            key={notification.itemId}
            className={`
                cursor-pointer relative p-2 rounded-lg transition-all duration-200
         
                ${isUnread ? 'bg-[#92bbff29] border-l-2 border-l-[#075ae3] shadow-sm' : (theme === 'dark' ? 'bg-[#f9f9f92b]' : 'bg-[#f9f9f9]')}
            `}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="space-y-1">
                <div className="flex items-center justify-between">
                    <h3 className={`text-sm font-semibold`}>
                        {t(notification.activity?.object?.type) || notification.activity?.object?.type}
                    </h3>
                    <div className="w-6 h-6 flex items-center justify-center">
                        {isHovered && !notification.read && (
                            <button
                                onClick={handleMarkAsRead}
                                disabled={isMarkingAsRead}
                                className="flex items-center justify-center p-1 rounded-full hover:bg-muted transition-colors"
                                aria-label={t('MARK_AS_READ') || 'Mark as read'}
                                title={t('MARK_AS_READ') || 'Mark as read'}
                            >
                                <CheckCircle2
                                    className={`w-4 h-4 text-primary ${isMarkingAsRead ? 'opacity-50' : ''}`}
                                />
                            </button>
                        )}
                    </div>
                </div>

                <p className="text-xs font-normal text-foreground">
                    {config?.message}
                </p>

                {config?.conversationId && (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 h-auto text-xs font-medium text-primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                            navigate(`/chat/${config.conversationId}`);
                        }}
                    >
                        {t('GO_TO_CHAT') || 'Go to chat'}
                    </Button>
                )}

                <p className="text-xs text-muted-foreground">
                    {formatTimestamp(notification.timestamp || notification.createdDate || '')}
                </p>
            </div>
        </div>
    )
}

export default GroupNotification


